/**
 * 项目管理状态 Slice
 * 发行项目管理、工作台状态
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { apiSlice } from '../api/apiSlice';

// 项目状态枚举
export enum ProjectStatus {
  DRAFT = 'draft',
  QUESTIONNAIRE = 'questionnaire',
  SCREENING = 'screening',
  ASSESSMENT = 'assessment',
  PREPARATION = 'preparation',
  APPROVAL = 'approval',
  ISSUANCE = 'issuance',
  LISTING = 'listing',
  ONGOING = 'ongoing',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  REJECTED = 'rejected',
}

// 项目类型枚举
export enum ProjectType {
  GREEN_BOND = 'green_bond',
  SUSTAINABILITY_BOND = 'sustainability_bond',
  SOCIAL_BOND = 'social_bond',
  TRANSITION_BOND = 'transition_bond',
}

// 发行阶段枚举
export enum IssuanceStage {
  INITIAL_QUESTIONNAIRE = 'initial_questionnaire',
  DETAILED_ASSESSMENT = 'detailed_assessment',
  GREEN_CERTIFICATION = 'green_certification',
  ISSUANCE_PREPARATION = 'issuance_preparation',
  REGULATORY_FILING = 'regulatory_filing',
  ROADSHOW = 'roadshow',
  PRICING = 'pricing',
  LISTING = 'listing',
  ONGOING_MANAGEMENT = 'ongoing_management',
}

// 项目接口
export interface Project {
  id: string;
  name: string;
  description: string;
  type: ProjectType;
  status: ProjectStatus;
  currentStage: IssuanceStage;

  // 基本信息
  issuer: {
    id: string;
    name: string;
    industry: string;
    rating?: string;
  };

  // 发行信息
  issuance: {
    amount: number; // 发行规模（万元）
    currency: string;
    maturity: number; // 期限（年）
    expectedRate?: number; // 预期利率
    useOfProceeds: string; // 资金用途
  };

  // 绿色认证
  greenCertification?: {
    standard: string; // 认证标准
    certifier: string; // 认证机构
    score: number; // 绿色评分
    validUntil: string; // 有效期
  };

  // 进度信息
  progress: {
    overall: number; // 整体进度百分比
    stages: Record<
      IssuanceStage,
      {
        status: 'pending' | 'in_progress' | 'completed' | 'skipped';
        completedAt?: string;
        notes?: string;
      }
    >;
  };

  // 关键任务
  keyTasks: ProjectTask[];

  // 项目团队
  team: ProjectTeamMember[];

  // 文档附件
  documents: ProjectDocument[];

  // 时间信息
  createdAt: string;
  updatedAt: string;
  expectedListingDate?: string;
  actualListingDate?: string;
}

// 项目任务接口
export interface ProjectTask {
  id: string;
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'in_progress' | 'completed' | 'blocked';
  assignedTo?: string;
  dueDate?: string;
  completedAt?: string;
  stage: IssuanceStage;
}

// 项目团队成员接口
export interface ProjectTeamMember {
  id: string;
  name: string;
  role: string;
  organization: string;
  email: string;
  phone?: string;
  avatar?: string;
}

// 项目文档接口
export interface ProjectDocument {
  id: string;
  name: string;
  type:
    | 'questionnaire'
    | 'assessment'
    | 'certification'
    | 'legal'
    | 'financial'
    | 'other';
  url: string;
  uploadedBy: string;
  uploadedAt: string;
  size: number;
  status: 'draft' | 'review' | 'approved' | 'rejected';
}

// 项目状态接口
export interface ProjectState {
  // 项目列表
  projects: Project[];

  // 当前项目
  currentProject: Project | null;

  // 筛选和搜索
  filters: {
    status: ProjectStatus[];
    type: ProjectType[];
    search: string;
  };

  // 分页
  pagination: {
    page: number;
    limit: number;
    total: number;
  };

  // 状态标识
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;

  // 错误信息
  error: string | null;

  // 统计信息
  stats: {
    total: number;
    byStatus: Record<ProjectStatus, number>;
    byType: Record<ProjectType, number>;
  };
}

// 初始状态
const initialState: ProjectState = {
  projects: [],
  currentProject: null,
  filters: {
    status: [],
    type: [],
    search: '',
  },
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
  },
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  error: null,
  stats: {
    total: 0,
    byStatus: {} as Record<ProjectStatus, number>,
    byType: {} as Record<ProjectType, number>,
  },
};

// 项目slice
const projectSlice = createSlice({
  name: 'project',
  initialState,
  reducers: {
    // 设置加载状态
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    // 设置创建状态
    setCreating: (state, action: PayloadAction<boolean>) => {
      state.isCreating = action.payload;
    },

    // 设置更新状态
    setUpdating: (state, action: PayloadAction<boolean>) => {
      state.isUpdating = action.payload;
    },

    // 设置错误
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // 清除错误
    clearError: state => {
      state.error = null;
    },

    // 设置项目列表
    setProjects: (state, action: PayloadAction<Project[]>) => {
      state.projects = action.payload;
    },

    // 设置当前项目
    setCurrentProject: (state, action: PayloadAction<Project | null>) => {
      state.currentProject = action.payload;
    },

    // 添加项目
    addProject: (state, action: PayloadAction<Project>) => {
      state.projects.unshift(action.payload);
    },

    // 更新项目
    updateProject: (
      state,
      action: PayloadAction<{ id: string; updates: Partial<Project> }>
    ) => {
      const { id, updates } = action.payload;
      const index = state.projects.findIndex(p => p.id === id);
      if (index !== -1) {
        state.projects[index] = { ...state.projects[index], ...updates };
      }

      // 如果更新的是当前项目，也要更新currentProject
      if (state.currentProject?.id === id) {
        state.currentProject = { ...state.currentProject, ...updates };
      }
    },

    // 删除项目
    removeProject: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      state.projects = state.projects.filter(p => p.id !== id);

      if (state.currentProject?.id === id) {
        state.currentProject = null;
      }
    },

    // 设置筛选条件
    setFilters: (
      state,
      action: PayloadAction<Partial<typeof initialState.filters>>
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    // 清除筛选
    clearFilters: state => {
      state.filters = {
        status: [],
        type: [],
        search: '',
      };
    },

    // 设置分页
    setPagination: (
      state,
      action: PayloadAction<Partial<typeof initialState.pagination>>
    ) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },

    // 设置统计信息
    setStats: (state, action: PayloadAction<typeof initialState.stats>) => {
      state.stats = action.payload;
    },

    // 更新项目进度
    updateProjectProgress: (
      state,
      action: PayloadAction<{
        projectId: string;
        stage: IssuanceStage;
        status: 'pending' | 'in_progress' | 'completed' | 'skipped';
        notes?: string;
      }>
    ) => {
      const { projectId, stage, status, notes } = action.payload;
      const project = state.projects.find(p => p.id === projectId);

      if (project) {
        project.progress.stages[stage] = {
          status,
          completedAt:
            status === 'completed' ? new Date().toISOString() : undefined,
          notes,
        };

        // 重新计算整体进度
        const stages = Object.values(project.progress.stages);
        const completedStages = stages.filter(
          s => s.status === 'completed'
        ).length;
        project.progress.overall = Math.round(
          (completedStages / stages.length) * 100
        );

        // 更新当前阶段
        if (status === 'completed') {
          const stageOrder = Object.values(IssuanceStage);
          const currentIndex = stageOrder.indexOf(stage);
          if (currentIndex < stageOrder.length - 1) {
            project.currentStage = stageOrder[currentIndex + 1];
          }
        } else if (status === 'in_progress') {
          project.currentStage = stage;
        }
      }

      // 同步更新当前项目
      if (state.currentProject?.id === projectId && project) {
        state.currentProject = { ...project };
      }
    },
  },
});

// 扩展API slice - 项目相关端点
export const projectApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    // 获取项目列表
    getProjects: builder.query<GetProjectsResponse, GetProjectsRequest>({
      query: params => ({
        url: '/project',
        params,
      }),
      providesTags: ['Project'],
    }),

    // 获取单个项目
    getProject: builder.query<GetProjectResponse, string>({
      query: id => `/project/${id}`,
      providesTags: (result, error, id) => [{ type: 'Project', id }],
    }),

    // 创建项目
    createProject: builder.mutation<
      CreateProjectResponse,
      CreateProjectRequest
    >({
      query: data => ({
        url: '/project',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Project'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        dispatch(projectSlice.actions.setCreating(true));
        try {
          const { data } = await queryFulfilled;
          if (data.success) {
            dispatch(projectSlice.actions.addProject(data.data));
            dispatch(projectSlice.actions.setCreating(false));
            dispatch(projectSlice.actions.clearError());
          }
        } catch (error: any) {
          dispatch(
            projectSlice.actions.setError(
              error.error?.data?.message || '创建项目失败'
            )
          );
          dispatch(projectSlice.actions.setCreating(false));
        }
      },
    }),

    // 更新项目
    updateProject: builder.mutation<
      UpdateProjectResponse,
      UpdateProjectRequest
    >({
      query: ({ id, ...data }) => ({
        url: `/project/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Project', id }],
      async onQueryStarted({ id, ...updates }, { dispatch, queryFulfilled }) {
        dispatch(projectSlice.actions.setUpdating(true));
        try {
          const { data } = await queryFulfilled;
          if (data.success) {
            dispatch(
              projectSlice.actions.updateProject({ id, updates: data.data })
            );
            dispatch(projectSlice.actions.setUpdating(false));
            dispatch(projectSlice.actions.clearError());
          }
        } catch (error: any) {
          dispatch(
            projectSlice.actions.setError(
              error.error?.data?.message || '更新项目失败'
            )
          );
          dispatch(projectSlice.actions.setUpdating(false));
        }
      },
    }),

    // 删除项目
    deleteProject: builder.mutation<DeleteProjectResponse, string>({
      query: id => ({
        url: `/project/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Project'],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(projectSlice.actions.removeProject(id));
          dispatch(projectSlice.actions.clearError());
        } catch (error: any) {
          dispatch(
            projectSlice.actions.setError(
              error.error?.data?.message || '删除项目失败'
            )
          );
        }
      },
    }),

    // 获取项目统计
    getProjectStats: builder.query<ProjectStatsResponse, void>({
      query: () => '/project/stats',
      providesTags: ['Project'],
    }),
  }),
});

// 导出actions和hooks
export const {
  setLoading,
  setCreating,
  setUpdating,
  setError,
  clearError,
  setProjects,
  setCurrentProject,
  addProject,
  updateProject,
  removeProject,
  setFilters,
  clearFilters,
  setPagination,
  setStats,
  updateProjectProgress,
} = projectSlice.actions;

export const {
  useGetProjectsQuery,
  useGetProjectQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useGetProjectStatsQuery,
} = projectApiSlice;

export default projectSlice.reducer;

// TypeScript类型定义
export interface GetProjectsRequest {
  page?: number;
  limit?: number;
  status?: ProjectStatus[];
  type?: ProjectType[];
  search?: string;
}

export interface GetProjectsResponse {
  success: boolean;
  data: {
    projects: Project[];
    total: number;
    page: number;
    limit: number;
  };
}

export interface GetProjectResponse {
  success: boolean;
  data: Project;
}

export interface CreateProjectRequest {
  name: string;
  description: string;
  type: ProjectType;
  issuance: {
    amount: number;
    currency: string;
    maturity: number;
    useOfProceeds: string;
  };
}

export interface CreateProjectResponse {
  success: boolean;
  data: Project;
}

export interface UpdateProjectRequest {
  id: string;
  name?: string;
  description?: string;
  status?: ProjectStatus;
  issuance?: Partial<Project['issuance']>;
  [key: string]: any;
}

export interface UpdateProjectResponse {
  success: boolean;
  data: Project;
}

export interface DeleteProjectResponse {
  success: boolean;
  message: string;
}

export interface ProjectStatsResponse {
  success: boolean;
  data: {
    total: number;
    byStatus: Record<ProjectStatus, number>;
    byType: Record<ProjectType, number>;
  };
}
